import { BaseWorkflowActivityAssignmentViewModel } from "../base-workflow-activity-assignment";
import { autoinject, computedFrom, observable } from "aurelia-framework";
import { WorkflowAssignmentsService } from "../../../../services/workflow/workflow-assignments-service";
import * as Enumerable from 'linq';
import { UserClientService } from "../../../../services/authentication/user-client.service";
import { UserSecurityInfoModel } from "../../../../models/authentication/token.models";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { I18N } from "aurelia-i18n";
import { RightShipOnboardingService } from "../../../../services/rightship-onboarding/rightship-onboarding.service";
import { AttachmentsHelper } from "../../../../utilities/attachments-helper";
import { AttachmentClientService } from "../../../../services/attachment/attachments-client.service";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { FileHelper } from "../../../../helpers/file-helper";
import { DialogServiceConstants } from "../../../../constants/basics/dialog.constants";
import { LookupValue } from "../../../../models/lookups/lookup.models";
import { RightShipWorkflowStateEnum } from "../../../../models/workflow/rightship.workflow.models";
import { NotificationClientService, NotificationType } from "../../../../services/basics/notification-client.service";
import { AttachmentHelper } from "../../../../helpers/attachment-helper";
import { VettingService } from "../../../../services/vetting/vetting.service";
import { RightShipOnboardingQuestionCommentService } from "../../../../services/rightship-onboarding/rightship-onboarding-question-comment.service";

@autoinject()
export class ActivityAssignment extends BaseWorkflowActivityAssignmentViewModel {

    public user: UserSecurityInfoModel;
    public disableAutoComplete: boolean = true;
    public actionSelected: string;
    public vettingContactEmails: { email: string, emailRecipientTypeId: number, isShipManager: boolean }[] = [];
    public vettingContactMessage: string;
    public vetId: number;
    public isWorkflowCancelled: boolean;
    public isAssignmentCancelled: boolean;
    public isAssignmentInvalidated: boolean;
    public recipients: any[] = [];
    public files: File[] = [];
    public attachments: any[] = [];
    public acceptableDayOnlyReasons: LookupValue[] = [];
    public acceptableDayOnlyReason: number;

    @observable()
    public showVettingContactView: boolean;

    @computedFrom('_model')
    public get isWorkflowStateAcceptebleDayOnly() {
        return this.workflowState.workflowStatusNotes == "Acceptable Day Only";
    }

    @computedFrom('_model')
    public get isAcceptableDayOnlyIncluded() {
        return this.assignment.configMeta && JSON.parse(this.assignment.configMeta).includeAcceptableDayOnly;
    }

    @computedFrom('_model')
    public get completedCount() {
        return this.workflowState.workflowAssignments.filter(x => x.stepNumber < this.assignment.stepNumber).length;
    }

    @computedFrom('_model')
    public get upcomingCount() {
        return this.workflowState.workflowAssignments.filter(x => x.stepNumber > this.assignment.stepNumber).length;
    }

    @computedFrom('_model')
    public get isAcceptableDayOnly() {
        return this.assignment.meta && JSON.parse(this.assignment.meta).acceptableDayOnly == true;
    }

    @computedFrom('_model')
    public get isReopened() {
        return this.assignment.meta && JSON.parse(this.assignment.meta).reopened == true;
    }

    @computedFrom('_model')
    public get isNeedMoreInfo() {
        return this.assignment.meta && JSON.parse(this.assignment.meta).needMoreInfo == true;
    }

    @computedFrom('_model')
    public get isContinueToNextStep() {
        return this.assignment.configMeta && JSON.parse(this.assignment.configMeta)?.continueToNextStep == true;
    }

    @computedFrom('isNeedMoreInfo')
    public get needMoreInfoReason() {

        if (this.isNeedMoreInfo) {

            const meta = JSON.parse(this.assignment.meta);

            if (meta.acceptableDayOnlyReason) {
                return this.acceptableDayOnlyReasons.filter(x => x.id == meta.acceptableDayOnlyReason)[0].name;
            }

        }

        return null;
    }

    @computedFrom('_model')
    public get perviousStepWasIncomplete(): boolean {
        if (!this.workflowState)
            return false;

        return Enumerable.from(this.workflowState.workflowAssignments)
            .orderBy(x => x.stepNumber).where(x => x.stepNumber == this.getCurrentStep() - 1).any(x => x.isAccepted == null);
    }

    @computedFrom('_model')
    public get recipientEmails(): string[] {
        
        const meta = JSON.parse(this.assignment.meta);
        return meta?.recipients ?? []
    }

    @computedFrom('_model')
    public get isPreviousNeeedMoreInfo() {
        if(!this.workflowState && !this.workflowState.workflowAssignments) {
            return false
        }
        const previousAssignments = this.workflowState.workflowAssignments.filter(x => x.stepNumber < this.assignment.stepNumber);
        const previousAssignmentStepNumber= Math.max(...previousAssignments.map(x => x.stepNumber));
        const previousAssignment = previousAssignments.filter(x => x.stepNumber ==previousAssignmentStepNumber)[0];
        
        return previousAssignment.meta && JSON.parse(previousAssignment.meta).needMoreInfo == true;
    } 

    constructor(workflowAssignmentsService: WorkflowAssignmentsService,
        dialogClientService: DialogClientService,
        private rightShipOnboardingService: RightShipOnboardingService,
        private attachmnetService: AttachmentClientService,
        private notificationService: NotificationClientService,
        private userClientService: UserClientService, i18n: I18N,
        protected vetClientService: VettingService,
        private questionCommentsService: RightShipOnboardingQuestionCommentService) {
        super(dialogClientService, i18n, workflowAssignmentsService, vetClientService);
        this.user = this.userClientService.currentUser;
    }

    public getCurrentStep(): number {
        const current = Enumerable.from(this.workflowState.workflowAssignments).where(x => x.isCurrentAssignment).select(x => x.stepNumber);
        return current.any() ? current.min() : null;
    }
    
    getSize(attachment) {
        if(attachment.fileSize) return AttachmentHelper.formatBytes(attachment.fileSize)
        return ""
    }

    onActivated() {
        this.vetId = this._model.item.vetId;
        const metaParsed = this.assignment.meta ? JSON.parse(this.assignment.meta) : {};
        this.vettingContactEmails = (metaParsed.vettingContactEmails || []).filter(x => !x.isShipManager);
        this.vettingContactMessage = metaParsed.vettingContactMessage || null;
        this.isWorkflowCancelled = this.isLastClosedAssignment && this.workflowState.state == RightShipWorkflowStateEnum.Cancelled;
        const meta = this.assignment && this.assignment.meta ? JSON.parse(this.assignment.meta) : {};
        this.isAssignmentCancelled = meta && (meta.cancelledBy || meta.cancelledBy == "") && !this.showIsReopened;
        this.isAssignmentInvalidated = meta && meta.invalidated && !this.showIsReopened && !this.assignment.isCurrentAssignment;

        this.assignment.assignmentState.actionTakerAnonText = 'Terminal Review';
        if(this.isNeedMoreInfo || (this.assignment.isAccepted && !this.isAcceptableDayOnly))
        {
            this.assignment.assignmentState.actionTakerAnonText = this.assignment.actionTaker ?? "Terminal Review"
            this.attachmnetService.getAttachments({entityId: this.assignment.workflowAssignmentId, entityType: EntityTypes.WorkflowAssignment}).then(x => {
                if(x && x.entityAttachments && x.entityAttachments.length > 0)
                {
                    this.attachments = x.entityAttachments;
                }                
            });
        }

        this.acceptableDayOnlyReasons = [];

        if (this.isHQ) {
            this.acceptableDayOnlyReasons.push(<LookupValue>{ name: "Requires Clarification", id: 1 });
            this.acceptableDayOnlyReasons.push(<LookupValue>{ name: "Acceptable Day Only MRR", id: 2 });
            this.acceptableDayOnlyReasons.push(<LookupValue>{ name: "No Landing", id: 3 });
        }
        else if (this.isHayPoint) {
            this.acceptableDayOnlyReasons.push(<LookupValue>{ name: "Requires Clarification", id: 1 });
            this.acceptableDayOnlyReasons.push(<LookupValue>{ name: "Conditionally Approved", id: 2 });
        }
    }

    showVettingContactViewChanged() {
        if (!this.showVettingContactView) {
            this.vettingContactEmails = [];
            this.vettingContactMessage = '';
        }
    }

    submit() {
        return this.accept();
    }

    accept() {
        this.actionSelected = "accept";
        this.questionCommentsService.showQuestionComments = true;
    }

    reject() {
        this.actionSelected = "reject";
        this.questionCommentsService.showQuestionComments = true;
    }

    needMoreInfo() {
        this.actionSelected = "need-more-info";
        this.questionCommentsService.showQuestionComments = true;
        this.rightShipOnboardingService.getRecipientsForNeedMoreInfo(this.assignment.workflowStateId).then(response => {
            this.recipients = response;
        });
    }

   
    acceptableDayOnly() {
        this.actionSelected = "acceptable-day-only";
    }

    resetAction() {
        this.actionSelected = null;
        this.questionCommentsService.showQuestionComments = false;
        this.assignment.remarks = null;
        this.vettingContactEmails = [];
        this.vettingContactMessage = null;
        this.files = [];
    }

    submitAction() {
        const remarks = [this.assignment.remarks]
            .concat(this.questionCommentsService.messages.map(x => this.questionCommentsService.questionTexts[x.guid] + "\n" + x.comment))
            .filter(x => x).join("\n\n");
        let promises: Promise<any>[] = [];
        if(this.questionCommentsService.messages.length > 0) 
        {
            promises.push(this.questionCommentsService.saveQuestionComments(true));
        }
        
        if (this.actionSelected == "accept") {
            const metaParsed = this.assignment.meta ? JSON.parse(this.assignment.meta) : {};
            metaParsed.needMoreInfo = false;
            metaParsed.acceptableDayOnly = false;
            metaParsed.vettingContactEmails = !this.isContinueToNextStep ? this.vettingContactEmails : [];
            metaParsed.vettingContactMessage = !this.isContinueToNextStep ? this.vettingContactMessage : null; 
            metaParsed.reopened = false;
            metaParsed.correspondenceEmails = this.correspondenceEmails;
            this.assignment.meta = JSON.stringify(metaParsed);

            return new Promise<void>((resolve, reject) => {
                promises = [
                    this.workflowAssignmentsService.executeAction(this.assignment.workflowStateId,
                        'terminal-review-activity',
                        'accept',
                        this.assignment.workflowAssignmentId,
                        null,
                        remarks,
                        this.assignment.meta,
                        this.files
                    )
                ].concat(promises);
            
                Promise.all(promises).then(response => {
                        this.reload(response[0].workflowState, response[0].canTakeOwnership, response[0].canReassign);
                        resolve();
                    }).catch(reject);
            });
        } else if (this.actionSelected == "reject") {
            const metaParsed = this.assignment.meta ? JSON.parse(this.assignment.meta) : {};
            metaParsed.needMoreInfo = false;
            metaParsed.acceptableDayOnly = false;
            metaParsed.vettingContactEmails = this.vettingContactEmails;
            metaParsed.vettingContactMessage = this.vettingContactMessage;
            metaParsed.reopened = false;
            this.assignment.meta = JSON.stringify(metaParsed);

            return new Promise<void>((resolve, reject) => {
                promises = [
                    this.workflowAssignmentsService.executeAction(this.assignment.workflowStateId,
                        'terminal-review-activity',
                        'reject',
                        this.assignment.workflowAssignmentId,
                        null,
                        remarks,
                        this.assignment.meta
                    )
                ].concat(promises);
            
                Promise.all(promises).then(response => {
                        this.reload(response[0].workflowState, response[0].canTakeOwnership, response[0].canReassign);
                        resolve();
                    }).catch(reject);
            });
        } else if (this.actionSelected == "need-more-info") {
            const metaParsed = this.assignment.meta ? JSON.parse(this.assignment.meta) : {};
            metaParsed.acceptableDayOnly = false;
            metaParsed.reopened = false;
            metaParsed.disableAutoComplete = this.disableAutoComplete;
            metaParsed.vettingContactEmails = this.vettingContactEmails;
            metaParsed.vettingContactMessage = this.vettingContactMessage;
            metaParsed.recipients = this.recipients;
            metaParsed.needMoreInfo = true;
            metaParsed.acceptableDayOnlyReason = this.acceptableDayOnlyReason;
            this.assignment.meta = JSON.stringify(metaParsed);

            return new Promise<void>((resolve, reject) => {

                if (!this.recipients || this.recipients.length == 0) {
                    this.notificationService.show("Error", "Please add at least one email", NotificationType.Error);
                    reject();
                }
                else {
                    promises = [
                        this.workflowAssignmentsService.executeAction(this.assignment.workflowStateId,
                            'terminal-review-activity',
                            'need-more-info',
                            this.assignment.workflowAssignmentId,
                            null,
                            remarks,
                            this.assignment.meta,
                            this.files
                        )
                    ].concat(promises);
                
                    Promise.all(promises).then(response => {
                        if(response[0].errorMessage) {
                            this.notificationService.show("", response[0].errorMessage, NotificationType.Error);
                        }
                        else {
                            this.notificationService.show("Need More Info Submitted", "Email was sent successfully", NotificationType.Success);
                            this.reload(response[0].workflowState, response[0].canTakeOwnership, response[0].canReassign);
                        }
                        resolve();
                    }).catch(reject);
                }
            });
        } else if (this.actionSelected == "acceptable-day-only") {
            const metaParsed = this.assignment.meta ? JSON.parse(this.assignment.meta) : {};
            metaParsed.acceptableDayOnly = true;
            metaParsed.reopened = false;
            metaParsed.disableAutoComplete = this.disableAutoComplete;
            metaParsed.vettingContactEmails = this.vettingContactEmails;
            metaParsed.vettingContactMessage = this.vettingContactMessage;
            metaParsed.needMoreInfo = false;
            this.assignment.meta = JSON.stringify(metaParsed);

            return new Promise<void>((resolve, reject) => {
                this.workflowAssignmentsService.executeAction(this.assignment.workflowStateId,
                    'terminal-review-activity',
                    'acceptable-day-only',
                    this.assignment.workflowAssignmentId,
                    null,
                    this.assignment.remarks,
                    this.assignment.meta
                ).then(response => {
                    this.reload(response.workflowState, response.canTakeOwnership, response.canReassign);
                    resolve();
                }).catch(reject);
            });
        }
    }

    public saveDraft(){
        return new Promise<void>((resolve, reject) => {
            const promises = [
                this.rightShipOnboardingService.saveDraftComment(this.assignment.workflowAssignmentId, this.assignment.remarks),
                this.questionCommentsService.saveQuestionComments()
            ];
            Promise.all(promises).then(() => {
                this.notificationService.show("Draft successfully saved", "Comment successfully saved. Do not forget to submit.", NotificationType.Success);
                resolve();
            })
            .catch(e => {
                this.notificationService.show("Draft save failed", "Please try again later.", NotificationType.Error);
                reject(e);
            });
        });
    }

    openFile(file) {
        if (file && file.path) {
            let path = AttachmentsHelper.getUrl(file.path);
            const downloadFile = !(this.isPDF(path) || this.isImage(path));
            this.attachmnetService.getSharedAccessSignatureToken(file.entityAttachmentId, downloadFile).then(sasToken => {
                if (sasToken) {
                    path += ((sasToken.startsWith("?") ? "" : "?") + sasToken);
                }

                if (!downloadFile) {
                    this.dialogClientService.show(DialogServiceConstants.URLs.ObjectViewer,
                        {
                            link: path,
                            doNotAddExtension: true
                        }, false, false, true);
                }
                else {
                    FileHelper.downloadFile(AttachmentsHelper.getUrl(path), file.documentName);
                }
            });
        }
    }

    public isPDF(link): boolean {
        return link.indexOf('.pdf') != -1;
    }

    public isImage(link): boolean {
        const imageExtensions = ["jpg", "jpeg", "png", "gif", "svg"];
        return imageExtensions.some(imageExtension => link.indexOf(imageExtension) != -1);
    }

    
    @computedFrom('_model')
    public get isHQ(){
        return this._model && this._model.entityTypeId == 963006;
    }

    @computedFrom('_model')
    public get isHayPoint(){        
        return this._model && this._model.entityTypeId == 876775;
    }

    @computedFrom("assignment", "assignment.meta", "isAssignmentCancelled")
    get cancelledBy() {        

        if (!this.isAssignmentCancelled) return null;

        const meta = this.assignment && this.assignment.meta ? JSON.parse(this.assignment.meta) : {};
    
        if (meta && meta.cancelledBy) {
            return meta.cancelledBy;
        }
        else return null;
    }
    
    @computedFrom("assignment", "isReopened")
    public get showIsReopened() {
        return !this.assignment.isCurrentAssignment && this.assignment.isAccepted && this.isReopened;
    }
}
